import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { fetchWishlistAPI } from "../api/apiWishlistPage";
import { getCookieRaw, resolveUid, getUserType, isInWishlistCookie, isInCartCookie } from "../utilities/cookieUtils";
import { getEncodedTimestamp } from "../utilities/dateUtils";

const safeJsonParse = <T>(str: string | null, fallback: T): T => {
  if (!str) return fallback;
  try {
    return JSON.parse(decodeURIComponent(str));
  } catch {
    return fallback;
  }
};

export interface WishlistProduct {
  variationCode: string;
  product: string;
  variationName: string;
  name: string;
  image: string;
  price: number;
  mrp: number;
  discount: number;
  mappingCode?: string;
  sku?: string;
  rating?: number;
  availableStock?: number;
  isStockOut?: boolean;
  isOutOfStock?: boolean;
  sellerPriceDTO?: any;
  isFavourite?: boolean;
  isInCart?: boolean;
}

export const useWishlistLogic = () => {
  const [wishlistProducts, setWishlistProducts] = useState<WishlistProduct[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [timestamp, setTimestamp] = useState(getEncodedTimestamp());

  // Subscribe to redux wishlist to remove items from local list when they are removed elsewhere
  const reduxWishlist = useSelector(
    (state: any) => state.wishlist?.items || []
  );

  const fetchWishlistItems = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const uid = resolveUid();
      const d = getEncodedTimestamp();
      setTimestamp(d);

      // Get user type to determine how to check status
      const userType = getUserType();

      // Get mapping codes from cookies for random/guest users
      const stored = safeJsonParse(getCookieRaw("wishlist"), []);
      // normalize and extract just codes
      const mappingCodes = stored.map((item: any) => {
        if (typeof item === "string") return item;
        return item.mappingCode || item.variationCode || item.productId || item.code || "";
      }).filter((c: string) => c);

      const result = await fetchWishlistAPI(uid, d, mappingCodes);

      // Server returns: { status: 200, messageCode, message, data: [...] }
      const payload = result && result.data ? result.data : [];

      if (Array.isArray(payload) && payload.length > 0) {
        const items = payload.map((item: any) => {
          // Extract data from sellerPriceDTO (primary source) or fallback to root level
          const sellerPriceDTO = item.sellerPriceDTO || item.sellerPrice || {};

          const mrp = sellerPriceDTO.mrp ?? item.mrp ?? item.MRP ?? 0;
          const price = sellerPriceDTO.price ?? item.price ?? item.sellingPrice ?? 0;
          const mappingCode = sellerPriceDTO.mappingCode || item.mappingCode || "";
          const sku = sellerPriceDTO.sku || item.sku || "";
          const rating = sellerPriceDTO.rating ?? item.rating ?? 0;
          const rootAvailableStock = Number(item.availableStock);
          const sellerAvailableStock = Number(sellerPriceDTO.availableStock);
          const hasRootAvailableStock = Number.isFinite(rootAvailableStock);
          const hasSellerAvailableStock = Number.isFinite(sellerAvailableStock);
          const availableStock = hasRootAvailableStock
            ? rootAvailableStock
            : hasSellerAvailableStock
              ? sellerAvailableStock
              : undefined;
          const isStockOut =
            Boolean(
              item.isStockOut ??
              item.isOutOfStock ??
              sellerPriceDTO.isStockOut ??
              sellerPriceDTO.isOutOfStock
            ) ||
            (hasRootAvailableStock && rootAvailableStock <= 0) ||
            (hasSellerAvailableStock && sellerAvailableStock <= 0);

          // For random users, check cookies; for others, use API response
          let isFavourite: boolean;
          let isInCart: boolean;

          if (userType === 'random') {
            // Random users: check cookies
            isFavourite = isInWishlistCookie(mappingCode);
            isInCart = isInCartCookie(mappingCode);
          } else {
            // Guest/logged-in users: use API response
            isFavourite = sellerPriceDTO.isFavourite ?? item.isFavourite ?? false;
            isInCart = sellerPriceDTO.isInCart ?? item.isInCart ?? false;
          }

          return {
            variationCode: String(item.variationCode ?? ""),
            name: item.product || item.name || "",
            variationName: item.variationName || "",
            image:
              item.image ||
              (Array.isArray(item.images) ? item.images[0]?.image : "") ||
              "",
            price,
            mrp,
            discount: mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0,
            mappingCode,
            sku,
            rating,
            availableStock,
            isStockOut,
            isOutOfStock: isStockOut,
            // Pass the entire sellerPriceDTO to the card
            sellerPriceDTO: {
              ...sellerPriceDTO,
              variationCode: item.variationCode || sellerPriceDTO.variationCode,
              mappingCode,
              availableStock,
              isStockOut,
              isOutOfStock: isStockOut,
              isFavourite,
              isInCart,
              price,
              mrp,
              sku,
              rating,
            },
            // Also keep root level for backward compatibility
            isFavourite,
            isInCart,
          };
        });

        setWishlistProducts(items);
      } else {
        setWishlistProducts([]);
      }
    } catch (err) {
      console.error("Wishlist load failed:", err);
      setWishlistProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWishlistItems();
  }, [fetchWishlistItems]);

  // Keep local wishlistProducts in sync with redux wishlist removals
  useEffect(() => {
    if (!Array.isArray(reduxWishlist) || reduxWishlist.length === 0) {
      // Don't clear local list just because redux is empty (it might be initializing)
      // The API fetch is the source of truth for the full page list
      return;
    }

    const ids = new Set(reduxWishlist.map((i: any) => String(i.variationCode)));

    // Only filter if we actually have a discrepancy that suggests removal
    // (This is a simplified approach; strictly speaking, we rely on re-fetching for accuracy,
    // but this helps immediate UI updates if redux changes elsewhere)
    setWishlistProducts((prev) => {
      // Only filter out items that are definitely NOT in the non-empty redux list
      // But wait, if we added an item via API and Redux is stale, this might hide it.
      // It's safer to only allow this effect to remove items if we are sure.
      // For now, let's keep the removal logic but ONLY if redux has items.
      return prev.filter((p) => ids.has(String(p.variationCode)));
    });
  }, [reduxWishlist]);

  return {
    wishlistProducts,
    loading,
    fetchWishlistItems,
  };
};
